import {
  LearningSummaryConnectionType,
  LearningSummaryType,
  SearchResultConnectionType,
  SearchResultType,
} from "@devforce/trailhead-client-gql";
import { QueryVariables, verifiedIncompleteResult } from "../util/filterHelper";
import { QueryAdapterValue, QueryAdapterOptions } from "common/graphqlAdapter";
import {
  CollectionConfig,
  CollectionQuery,
  CollectionState,
  getCollectionItem,
} from "common/contentCollection"; // Actually in th-components (src/elements/common/contentCollection/contentCollection.ts:227)

export type LearningCollectionQueryOptions = QueryAdapterOptions & {
  collectionKey: string;
};

export interface LearningCollectionCard {
  config: CollectionConfig | null;
  pageLocation: string;
  queryMode: "append" | null;
  passEventUp: (e: CustomEvent) => void;
}

export const DEFAULT_PAGE_COLLECTION_SIZE = 25;

interface Data {
  [k: string]: LearningSummaryConnectionType | SearchResultConnectionType;
}

export class LearningCollectionQuery
  implements CollectionQuery<QueryAdapterValue<Data>>
{
  config: LearningCollectionQueryOptions;
  clickAnalyticsPayload = new Map<string, string>();
  searchAnalyticsPayload: string | null = null;

  get collectionKey(): keyof (QueryAdapterValue<Data> & { data: Data }) {
    return this.config.collectionKey as keyof (QueryAdapterValue<Data> & {
      data: Data;
    });
  }

  constructor(config: LearningCollectionQueryOptions) {
    this.config = config;
  }

  getCollection(result: QueryAdapterValue<Data>) {
    const collection =
      (result.data && result.data[this.collectionKey]) || undefined;

    // Avoid dispatching count events for non-search results
    if (collection && collection.__typename !== "SearchResultConnection") {
      this.getTotalItemCount = undefined;
    }

    return collection;
  }

  getQueryOptions(state?: CollectionState) {
    return {
      query: this.config.query,
      variables: {
        after: state?.endCursor ?? undefined,
        ...this.config.variables,
      },
    };
  }

  getCollectionItemWrapper(node: LearningSummaryType | SearchResultType) {
    if (node.__typename === "SearchResult")
      return getCollectionItem(node.learning!);
    return getCollectionItem(node as LearningSummaryType);
  }

  collectSearchAnalytics(node: SearchResultType) {
    if (!node || !node.learning?.id || !node.clickAnalyticsPayload) return node;

    this.clickAnalyticsPayload.set(
      node.learning.id,
      node.clickAnalyticsPayload
    );
    return node;
  }

  getCollectionItems(result: QueryAdapterValue<Data>) {
    const collection = this.getCollection(result);
    const edges = (collection && collection.edges) || [];

    this.searchAnalyticsPayload =
      (collection as SearchResultConnectionType)?.searchAnalyticsPayload ??
      null;

    return edges
      .map((edge) => edge!.node!)
      .map((node) => this.collectSearchAnalytics(node as SearchResultType))
      .map((node) => this.getCollectionItemWrapper(node))
      .filter((node) => node != null)
      .filter((node) => {
        const variables: QueryVariables = this.config.variables;
        const progressFilter = variables?.filter?.progress;
        return verifiedIncompleteResult(node, progressFilter);
      });
  }

  getState(result: QueryAdapterValue<Data>) {
    return {
      endCursor: this.getCollection(result)?.pageInfo.endCursor,
    };
  }

  getClickAnalyticsEventData(id: string) {
    return this.clickAnalyticsPayload.get(id);
  }

  hasMore(result: QueryAdapterValue<Data>) {
    return this.getCollection(result)?.pageInfo.hasNextPage;
  }

  getTotalItemCount?: (result: QueryAdapterValue<Data>) => number | undefined =
    (result: QueryAdapterValue<Data>) => {
      const { totalCount } = this.getCollection(
        result
      ) as SearchResultConnectionType;

      return typeof totalCount === "number" ? totalCount : undefined;
    };
}
